/* Keep closed app launchers clear of the S10 mobile comparison. */
(() => {
  'use strict';

  const key = '__acComparisonFocus';
  if (window[key]) {
    window[key].refresh();
    return;
  }

  const sectionSelector = '.ac-s10-comparison';
  const teaserSelector = '[data-testid="animated-teaser"]';
  const launcherSelector = '.BeaconFabButtonFrame';
  const appSelector = `${teaserSelector}, [class*="kl-teaser-"], .Beacon, .BeaconContainer, ${launcherSelector}`;
  const marker = 'data-ac-comparison-muted';
  const mobile = window.matchMedia('(max-width: 699px)');
  const sections = new Map();
  const muted = new Set();
  const unloading = new WeakSet();
  let frame = 0;
  let needsRefresh = true;

  // A section has entered the reading area after clearing either 64px edge.
  const intersection = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (sections.has(entry.target)) {
          sections.set(entry.target, entry.isIntersecting && entry.intersectionRect.height > 0 && entry.intersectionRect.width > 0);
        }
      });
      schedule();
    }, { rootMargin: '-64px 0px -64px 0px', threshold: 0 })
    : null;

  function inReadingArea(section) {
    const rect = section.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 64 && rect.top < window.innerHeight - 64;
  }

  function refreshSections() {
    const current = new Set(Array.from(document.querySelectorAll(sectionSelector)).filter(section => !unloading.has(section)));
    sections.forEach((visible, section) => {
      if (!current.has(section)) {
        intersection?.unobserve(section);
        sections.delete(section);
      }
    });
    current.forEach(section => {
      if (!sections.has(section)) intersection?.observe(section);
      sections.set(section, inReadingArea(section));
      section.querySelectorAll('.ac-s10-comparison__scroll').forEach(scroll => {
        const value = mobile.matches ? '-1' : '0';
        if (scroll.getAttribute('tabindex') !== value) scroll.setAttribute('tabindex', value);
      });
    });
    needsRefresh = false;
  }

  function supportIsOpen(launcher) {
    // Keep the launcher available when it is the open window's Close control.
    if (launcher.querySelector('iframe[title="Help Scout Beacon - Close"]')) return true;
    return Array.from(document.querySelectorAll('.BeaconContainer')).some(panel => {
      if (panel.hidden || panel.getAttribute('aria-hidden') === 'true') return false;
      const style = window.getComputedStyle(panel);
      return style.display !== 'none' && style.visibility !== 'hidden' && panel.getClientRects().length > 0;
    });
  }

  function update() {
    frame = 0;
    if (needsRefresh) refreshSections();
    if (!intersection) sections.forEach((visible, section) => sections.set(section, inReadingArea(section)));
    const reading = mobile.matches && Array.from(sections).some(([section, visible]) => section.isConnected && visible);
    const next = new Map();

    if (reading) {
      document.querySelectorAll(teaserSelector).forEach(teaser => {
        const wrapper = teaser.closest('[class*="kl-teaser-"]');
        // Campaign IDs may change; never mark a popup, form, or its ancestor.
        if (wrapper && Array.from(wrapper.classList).some(name => name.startsWith('kl-teaser-'))
          && !wrapper.closest('[role="dialog"], [aria-modal="true"]')
          && !wrapper.querySelector('[role="dialog"], [aria-modal="true"], form')) {
          next.set(wrapper, 'teaser');
        }
      });
      document.querySelectorAll(launcherSelector).forEach(launcher => {
        if (!supportIsOpen(launcher)) next.set(launcher, 'support');
      });
    }

    muted.forEach(element => {
      if (!next.has(element)) {
        element.removeAttribute(marker);
        muted.delete(element);
      }
    });
    next.forEach((kind, element) => {
      if (element.getAttribute(marker) !== kind) element.setAttribute(marker, kind);
      muted.add(element);
    });
  }

  function schedule(refresh = false) {
    needsRefresh = needsRefresh || refresh;
    if (!frame) frame = window.requestAnimationFrame(update);
  }

  function containsRelevantElement(node) {
    return node.nodeType === 1 && (node.matches(`${sectionSelector}, ${appSelector}`)
      || node.querySelector(`${sectionSelector}, ${appSelector}`));
  }

  const mutations = new MutationObserver(records => {
    const relevant = records.some(record => {
      if (record.type === 'attributes') return record.target.closest(`${appSelector}, [${marker}]`);
      return (record.target.nodeType === 1 && record.target.closest(appSelector))
        || Array.from(record.addedNodes).some(containsRelevantElement)
        || Array.from(record.removedNodes).some(containsRelevantElement);
    });
    if (relevant) schedule(true);
  });

  function start() {
    mutations.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'title', 'hidden', 'aria-hidden', 'role', 'aria-modal', 'data-testid']
    });
    schedule(true);
  }

  window[key] = { refresh: () => schedule(true) };
  mobile.addEventListener('change', () => schedule(true));
  window.addEventListener('resize', () => schedule(true), { passive: true });
  window.addEventListener('pageshow', () => schedule(true));
  if (!intersection) window.addEventListener('scroll', () => schedule(), { passive: true });
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.(sectionSelector)) unloading.delete(event.target);
    event.target.querySelectorAll?.(sectionSelector).forEach(section => unloading.delete(section));
    schedule(true);
  });
  document.addEventListener('shopify:section:unload', event => {
    sections.forEach((visible, section) => {
      if (event.target === section || event.target.contains(section)) unloading.add(section);
    });
    schedule(true);
  });
  document.addEventListener('shopify:section:reorder', () => schedule(true));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
